"use client";

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { injectMetaTags } from "@/lib/seoHelpers";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import { insuranceSections, insuranceFaq, insuranceServiceLinks } from "@/mocks/blogInsurance";

export default function BlogInsuranceArticle() {
  const { t, currentLang } = useLanguage();
  useHreflang();
  const isEs = currentLang === "es";

  useEffect(() => {
    const title = isEs
      ? "Reclamos de Seguro por Daños a Ventanas Miami — Guía de Documentación"
      : "Window Damage Insurance Claims Miami — Documentation Guide";
    const desc = t("blog.insurance.metaDesc") as string;

    document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);

    const baseUrl = "https://impact-window-repair-miami.com";
    const path = isEs ? "/es/blog/descuento-seguro-ventanas-impacto/" : "/blog/insurance-discount-impact-windows/";
    const url = `${baseUrl}${path}`;

    const cleanupSeo = injectMetaTags({
      title,
      description: desc,
      url,
      image: "https://impact-window-repair-miami.com/og/og-blog-insurance.jpg",
      imageAlt: isEs ? "Reclamos de seguro daños a ventanas Miami" : "Window damage insurance claims Miami",
      breadcrumbs: [
        { name: isEs ? "Inicio" : "Home", item: baseUrl + "/" },
        { name: "Blog", item: baseUrl + "/blog/" },
        { name: t("blog.insurance.h1") as string, item: url }
      ],
      type: "article",
      articleAuthor: "Windows and Doors Repair LLC",
      articlePublisher: baseUrl,
      articleSection: isEs ? "Información de Seguro" : "Insurance Information",
      articlePublishedTime: "2026-05-18",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: t("blog.insurance.h1"),
      inLanguage: isEs ? "es-US" : "en-US",
      author: {
        "@type": "Organization",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
      },
      datePublished: "2026-05-18",
      dateModified: "2026-05-18",
      publisher: {
        "@type": "Organization",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
      },
      description: desc,
    };

    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "blogpost-schema";
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);

    return () => {
      cleanupSeo();
      const el = document.getElementById("blogpost-schema");
      if (el) document.head.removeChild(el);
    };
  }, [currentLang, isEs, t]);

  const getSection = (key: string) => {
    const s = insuranceSections.find((sec) => sec.key === key);
    return s ? (isEs ? s.bodyEs : s.bodyEn) : [];
  };

  const blogPrefix = isEs ? "/es/blog/" : "/blog/";
  const homeLink = isEs ? "/es/" : "/";

  return (
    <main className="bg-white">
      <div className="max-w-5xl mx-auto px-6">

        {/* Article Header */}
        <section className="py-12">
          <nav className="mb-4" aria-label="Breadcrumb">
            <Link to={blogPrefix} className="text-sm text-[#047857] hover:text-[#ea580c]">
              Blog
            </Link>
            <span className="text-sm text-gray-400 mx-2">&gt;</span>
            <span className="text-sm text-gray-500">{t("blog.catInsurance")}</span>
          </nav>

          <span className="inline-block bg-[#fbbf24] text-[#064e3b] uppercase tracking-widest text-xs px-3 py-1 rounded">
            {t("blog.catInsurance")}
          </span>

          <h1 className="mt-4 text-4xl font-bold text-[#064e3b] leading-tight">
            {t("blog.insurance.h1")}
          </h1>

          <p className="mt-4 text-sm text-gray-500">
            {t("blog.publishedOn")} May 2026 &middot; 8 {t("blog.minRead")}
          </p>

          <img
            src="https://static.readdy.ai/image/60b7a1bdc124301cce40c5bb19c672c9/cf5c185cbe5cddafe327fd18dd4d3beb.jpeg"
            alt={isEs ? "Reclamos de seguro daños a ventanas Florida" : "Florida homeowner insurance window damage claims"}
            className="mt-8 w-full rounded-xl object-cover"
            width={1000}
            height={500}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://placehold.co/1000x500/064e3b/fbbf24?text=Insurance";
            }}
          />
        </section>

        {/* Disclaimer */}
        <div className="bg-[#ea580c]/10 border-l-4 border-[#ea580c] rounded-r-xl p-6 mb-10">
          <p className="text-[#064e3b] font-semibold text-sm leading-relaxed">
            {isEs
              ? "Importante: Windows and Doors Repair LLC NO proporciona servicios de reclamos de seguro ni inspecciones de mitigación de viento. Este artículo es solo para educación general. Para inspecciones, contacte un inspector certificado en mitigación de viento en Florida. Para reclamos, trabaje con su proveedor de seguro y contratistas con licencia."
              : "Important: Windows and Doors Repair LLC does NOT provide insurance claim services or wind mitigation inspections. This article is for general education only. For inspections, contact a Florida-certified wind mitigation inspector. For claims, work with your insurance provider and licensed contractors."}
          </p>
        </div>

        {/* Intro */}
        <section className="mb-12">
          {getSection("intro").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* What We Document */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.insurance.h2What")}
          </h2>
          {getSection("whatDocument").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* What We Do Not Do */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.insurance.h2NotDo")}
          </h2>
          {getSection("whatNotDo").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* Typical Claim Process */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.insurance.h2Process")}
          </h2>
          {getSection("typicalProcess").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* Hurricane vs Non-Hurricane Deductibles */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.insurance.h2Deductibles")}
          </h2>
          {getSection("deductibles").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* When to Hire a Public Adjuster */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.insurance.h2When")}
          </h2>
          {getSection("whenHire").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* Service Links */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-4">
            {isEs ? "Servicios Relacionados" : "Related Services"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {insuranceServiceLinks.map((s, i) => (
              <Link
                key={i}
                to={isEs ? `/es${s.to}` : s.to}
                className="bg-white border border-[#047857]/20 rounded-lg p-4 text-[#064e3b] font-semibold text-sm hover:bg-[#fefce8] hover:border-[#047857] transition whitespace-nowrap"
              >
                {isEs ? s.labelEs : s.labelEn} &rarr;
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-6">
            {isEs ? "Preguntas Frecuentes" : "Common Questions"}
          </h2>
          <div className="space-y-4">
            {insuranceFaq.map((faq, i) => (
              <div key={i} className="border border-[#064e3b]/10 rounded-lg p-5">
                <p className="font-semibold text-[#064e3b] mb-2">{isEs ? faq.qEs : faq.qEn}</p>
                <p className="text-sm text-[#374151] leading-relaxed">{isEs ? faq.aEs : faq.aEn}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#047857] text-white py-16 my-12 -mx-6 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold">
              {isEs ? "Mantenga Sus Ventanas Funcionales" : "Keep Your Windows Maintained"}
            </h2>
            <p className="mt-4 text-white/90 max-w-xl mx-auto">
              {isEs
                ? "El mantenimiento anual mantiene sus ventanas funcionales, limpias y listas para inspección. Reserve una visita de mantenimiento en Miami-Dade."
                : "Annual maintenance keeps your windows functional, clean, and ready for inspection. Book a maintenance visit across Miami-Dade."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+17863774452"
                className="inline-block bg-white text-[#047857] font-bold px-8 py-3 rounded-lg hover:bg-[#fefce8] transition whitespace-nowrap"
              >
                {isEs ? "Llame al (786) 377-4452" : "Call (786) 377-4452"}
              </a>
              <Link
                to={homeLink}
                className="inline-block border-2 border-white text-white font-bold px-8 py-3 rounded-lg hover:bg-white hover:text-[#047857] transition whitespace-nowrap"
              >
                {isEs ? "Obtener Cotización Telefónica" : "Get Phone Quote"}
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-6">{t("blog.relatedArticles")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <Link
              to={`${blogPrefix}window-repair-cost-miami/`}
              className="bg-white rounded-lg border border-[#064e3b]/10 p-5 hover:shadow-lg hover:border-[#fbbf24]/50 transition block"
            >
              <span className="inline-block bg-[#fbbf24] text-[#064e3b] text-xs uppercase tracking-widest px-3 py-1 rounded">
                {t("blog.catPricing")}
              </span>
              <h3 className="font-bold text-[#064e3b] mt-3 text-lg leading-snug">
                {t("blog.cost.h1")}
              </h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                {t("blog.articleCost.excerpt")}
              </p>
              <span className="inline-block mt-4 text-[#047857] text-sm font-medium hover:text-[#ea580c]">
                {t("blog.readMore")}
              </span>
            </Link>
            <Link
              to={`${blogPrefix}impact-windows-vs-shutters/`}
              className="bg-white rounded-lg border border-[#064e3b]/10 p-5 hover:shadow-lg hover:border-[#fbbf24]/50 transition block"
            >
              <span className="inline-block bg-[#fbbf24] text-[#064e3b] text-xs uppercase tracking-widest px-3 py-1 rounded">
                {t("blog.catComparison")}
              </span>
              <h3 className="font-bold text-[#064e3b] mt-3 text-lg leading-snug">
                {t("blog.articleShutters.title")}
              </h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                {t("blog.articleShutters.excerpt")}
              </p>
              <span className="inline-block mt-4 text-[#047857] text-sm font-medium hover:text-[#ea580c]">
                {t("blog.readMore")}
              </span>
            </Link>
            <Link
              to={`${blogPrefix}hvhz-code-noa-explained/`}
              className="bg-white rounded-lg border border-[#064e3b]/10 p-5 hover:shadow-lg hover:border-[#fbbf24]/50 transition block"
            >
              <span className="inline-block bg-[#fbbf24] text-[#064e3b] text-xs uppercase tracking-widest px-3 py-1 rounded">
                {t("blog.catCodes")}
              </span>
              <h3 className="font-bold text-[#064e3b] mt-3 text-lg leading-snug">
                {t("blog.articleCodes.title")}
              </h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                {t("blog.articleCodes.excerpt")}
              </p>
              <span className="inline-block mt-4 text-[#047857] text-sm font-medium hover:text-[#ea580c]">
                {t("blog.readMore")}
              </span>
            </Link>
          </div>
        </section>

      </div>
    </main>
  );
}